import { DefaultFocusStatusModel } from "../../../models/DefaultFocusStatusModel";
import { KeyboardNavigation } from "./KeyboardNavigation";

export class TextAnswerFieldNavigation extends KeyboardNavigation {
    variableIds =
        this.question?.parent?.columns?.flatMap(column =>
            column.variables?.map(variable => variable.id) ?? []
        ) ?? [];
    sequencedRowVariableIds =
        this.question?.parent?.sequencedRowVariableIds ?? [];
    sequencedColumnVariableIds =
        this.question?.parent?.sequencedColumnVariableIds ?? [];
    noOfColumns = this.question?.parent?.columns?.length || 0;

    findNextFocusableElementWithinSameVariable(
        currentElement: DefaultFocusStatusModel,
        eventKey: string,
    ): DefaultFocusStatusModel | null {
        const index = this.defaultFocusStatus.statuses.indexOf(currentElement);

        if (this.isHybridGrid && (eventKey === "ArrowDown" || eventKey === "ArrowUp")) {
            return this.findNextFocusableElementWithinSameVariableCommonForSwitchingRowsForHybridGrid(
                currentElement,
                eventKey,
            );
        }

        if (this.isRunningTotal) {
            const rowIndex = this.sequencedRowVariableIds.findIndex(str => str.includes(currentElement.variableId));
            const columnIndex = this.sequencedColumnVariableIds.findIndex(str => str.includes(currentElement.variableId));

            const isLastRowInVariable = rowIndex === this.sequencedRowVariableIds.length - 1;
            const isFirstRowInVariable = rowIndex === 0;

            if ((eventKey === "ArrowDown" && isLastRowInVariable) || (eventKey === "ArrowUp" && isFirstRowInVariable)) {
                return null;
            }

            if (columnIndex === -1 || (columnIndex === 0 && eventKey === "ArrowLeft") ||
                (columnIndex === this.sequencedRowVariableIds[rowIndex].split(',').length - 1 && eventKey === "ArrowRight")) {
                return null;
            }

            if (eventKey === "ArrowRight") {
                const elementFocusStatus = this.defaultFocusStatus.statuses[index + 1];
                if (this.sequencedRowVariableIds.findIndex(str => str.includes(elementFocusStatus.variableId)) > -1) {
                    return elementFocusStatus;
                }
                return null;
            } else if (eventKey === "ArrowLeft") {
                const elementFocusStatus = this.defaultFocusStatus.statuses[index - 1];
                if (this.sequencedRowVariableIds.findIndex(str => str.includes(elementFocusStatus.variableId)) > -1) {
                    return elementFocusStatus;
                }
                return null;
            }

            if ((eventKey === "ArrowDown" && !isLastRowInVariable) || (eventKey === "ArrowUp" && !isFirstRowInVariable)) {
                const nextRowIndex = eventKey === "ArrowDown" ? rowIndex + 1 : rowIndex - 1;
                const nextRowVariableIds = new Set(this.sequencedRowVariableIds[nextRowIndex].split(','));
                for (let i = 0; i < this.defaultFocusStatus.statuses.length; i++) {
                    const elementFocusStatus = this.defaultFocusStatus.statuses[i];
                    if (
                        nextRowVariableIds.has(elementFocusStatus.variableId.toString()) &&
                        elementFocusStatus.variableId !== currentElement.variableId &&
                        elementFocusStatus.elementId !== currentElement.elementId
                    ) {
                        return elementFocusStatus;
                    }
                }
            }

            return null;
        } else {
            let nextVariableId = currentElement.variableId;
            const currentVariableIndex = this.variableIds.indexOf(currentElement.variableId);
            const isLastRowInVariable = currentVariableIndex === this.variableIds.length - 1;
            const isLastColumnInVariable =
                this.variableIds.length <
                currentVariableIndex + this.variableIds.length / this.noOfColumns;
            const isFirstColumnInVariable =
                currentVariableIndex - this.variableIds.length / this.noOfColumns < 0;

            if (eventKey === "ArrowDown" && isLastRowInVariable) {
                return null;
            }

            if (
                eventKey === "ArrowDown" &&
                currentVariableIndex !== -1 &&
                currentVariableIndex < this.variableIds.length - 1
            ) {
                nextVariableId = this.variableIds[currentVariableIndex + 1];
            } else if (eventKey === "ArrowUp" && currentVariableIndex > 0) {
                nextVariableId = this.variableIds[currentVariableIndex - 1];
            } else if (eventKey === "ArrowRight" && !isLastColumnInVariable) {
                nextVariableId = this.variableIds[
                    currentVariableIndex + this.variableIds.length / this.noOfColumns
                ];
            } else if (eventKey === "ArrowLeft" && !isFirstColumnInVariable) {
                nextVariableId = this.variableIds[
                    currentVariableIndex - this.variableIds.length / this.noOfColumns
                ];
            }

            if (eventKey === "ArrowDown" || eventKey === "ArrowRight" || eventKey === "ArrowUp") {
                for (let i = 0; i < this.defaultFocusStatus.statuses.length; i++) {
                    const elementFocusStatus = this.defaultFocusStatus.statuses[i];
                    const indexCondition =
                        eventKey === "ArrowDown" || eventKey === "ArrowRight" ? i > index : i < index;

                    if (
                        indexCondition &&
                        elementFocusStatus.variableId === nextVariableId &&
                        elementFocusStatus.elementId !== currentElement.elementId
                    ) {
                        return elementFocusStatus;
                    }
                }
            } else if (eventKey === "ArrowLeft") {
                for (let i = this.defaultFocusStatus.statuses.length - 1; i >= 0; i--) {
                    const elementFocusStatus = this.defaultFocusStatus.statuses[i];
                    if (
                        i < index &&
                        elementFocusStatus.variableId === nextVariableId &&
                        elementFocusStatus.elementId !== currentElement.elementId
                    ) {
                        return elementFocusStatus;
                    }
                }
            }
        }
    }

    findNextFocusableElementWithinNextVariable(
        currentElement: DefaultFocusStatusModel,
    ): DefaultFocusStatusModel | null {
        const index = this.defaultFocusStatus.statuses.indexOf(currentElement);

        if (this.isHybridGrid) {
            return this.findNextFocusableElementWithinNextVariableCommonForHybridGrid(
                currentElement,
            );
        }

        for (let i = index + 1, len = this.defaultFocusStatus.statuses.length; i < len; i++) {
            const elementFocusStatus = this.defaultFocusStatus.statuses[i];
            const variableCondition =
                this.variableIds && this.variableIds.length > 0 && this.isRunningTotal
                    ? !this.variableIds.includes(elementFocusStatus.variableId)
                    : elementFocusStatus.variableId !== currentElement.variableId;
            if (elementFocusStatus.elementRef?.current &&
                (variableCondition || (elementFocusStatus.variableId === currentElement.variableId &&
                    currentElement.elementRef?.current?.tagName != elementFocusStatus.elementRef?.current?.tagName))
            ) {
                return this.getFocusableElement(elementFocusStatus, true);
            }
        }

        return null;
    }

    findLastFocusableElementWithinPreviousVariable(
        currentElement: DefaultFocusStatusModel,
    ): DefaultFocusStatusModel | null {
        return this.findLastFocusableElementWithinPreviousVariableCommon(currentElement, this.variableIds);
    }
}
